import {
  type BatchDeleteInboxDraftsMutation,
  type FetchResult,
  type MutationResult,
  useBatchDeleteInboxDraftsMutation,
} from "@qw/graphql";
import { useOpenConfirmation, useToast } from "@qw/ui";
import { useCallback } from "react";
import { useDraftsCompose } from "./use-drafts-compose";

type UseBatchDeleteDraftsReturn = [
  (draftIds: string[]) => Promise<FetchResult<BatchDeleteInboxDraftsMutation> | undefined>,
  MutationResult<BatchDeleteInboxDraftsMutation>,
];

export const useBatchDeleteDrafts = (): UseBatchDeleteDraftsReturn => {
  const { toast } = useToast();
  const { openConfirmation } = useOpenConfirmation();
  const { closeDrafts } = useDraftsCompose();

  const [batchDeleteDraftsMutation, result] = useBatchDeleteInboxDraftsMutation({
    refetchQueries: ["InboxDrafts", "InboxConversations", "InboxDraftsCount"],
  });

  const batchDeleteDrafts = useCallback(
    async (draftIds: string[]) => {
      const confirmed = await openConfirmation({
        title: `Discard ${draftIds.length} drafts`,
        description: "Are you sure you want to discard these drafts? This action cannot be undone.",
        buttonProps: { theme: "destructive", children: "Discard" },
      });

      if (!confirmed) {
        return;
      }

      return batchDeleteDraftsMutation({
        variables: { input: { draftIds } },
        onCompleted: (data) => {
          if (!data?.batchDeleteInboxDrafts) {
            throw new Error("Failed to discard drafts");
          }

          closeDrafts(draftIds);

          toast({
            variant: "default",
            title: `Discarded ${draftIds.length} drafts`,
          });
        },
        onError: (error) => {
          toast({
            title: `Failed to discard ${draftIds.length} drafts`,
            description: error instanceof Error ? error.message : "An unexpected error occurred",
            variant: "destructive",
          });
        },
      });
    },
    [batchDeleteDraftsMutation, openConfirmation, toast, closeDrafts]
  );

  return [batchDeleteDrafts, result];
};
